<script setup lang="ts">
import { SessionResults } from '@/components/game/session-results/index.js';
import type { SessionResultsProps, SessionResultValue } from '@/components/game/session-results/types.js';
import { useConfetti } from '@/components/confetti/use-confetti.ts';
import { useFlags } from '@/composables/use-feature-flags.ts';
import { useSessionStore } from '@/stores/session.ts';
import { Flags } from '@pokerly/shared-common';
import { PButton, PSeparator } from '@pokerly/ui';
import { computed, onBeforeUnmount, shallowRef, watch } from 'vue';

const RESULTS_PANEL_EXIT_DURATION_MS = 300;

const sessionStore = useSessionStore();
const { launch: launchConfetti } = useConfetti();
const pok48Flag = useFlags(Flags.POK_48_ADD_A_REPLAY_FUNCTIONALITY);

const totalVoters = computed(() => sessionStore.voters.length);
const modeVoters = computed(() => {
    const modeValue = sessionStore.statistics.mode;

    if (modeValue === null) {
        return 0;
    }

    return sessionStore.statistics.groupedVotes[modeValue] ?? 0;
});
const chartData = computed(() =>
    Object.entries(sessionStore.statistics.groupedVotes).map(([value, count]) => ({
        value: Number.isNaN(Number(value)) ? value : Number(value),
        votes: count,
    })),
);
const liveSessionResults = computed<SessionResultsProps>(() => ({
    suggestion: sessionStore.statistics.suggestion,
    estimationOptions: [...sessionStore.estimationOptions],
    mode: sessionStore.statistics.mode,
    median: sessionStore.statistics.median,
    votesCasted: sessionStore.statistics.votesCasted,
    totalVoters: totalVoters.value,
    modeVoters: modeVoters.value,
    hasNumericVotes: sessionStore.statistics.hasNumericVotes,
    chartData: chartData.value.map((item) => ({ ...item })),
}));
const cachedSessionResults = shallowRef<SessionResultsProps | null>(null);
const displayedSessionResults = computed(() => cachedSessionResults.value ?? liveSessionResults.value);

let clearResultsCacheTimeout: ReturnType<typeof setTimeout> | undefined;

function cloneSessionResults(results: SessionResultsProps): SessionResultsProps {
    return {
        ...results,
        estimationOptions: [...results.estimationOptions],
        chartData: results.chartData.map((item) => ({ ...item })),
    };
}

function clearPendingResultsCacheReset() {
    if (clearResultsCacheTimeout === undefined) {
        return;
    }

    clearTimeout(clearResultsCacheTimeout);
    clearResultsCacheTimeout = undefined;
}

watch(
    liveSessionResults,
    (results) => {
        if (!sessionStore.isRoundClosed) {
            return;
        }

        cachedSessionResults.value = cloneSessionResults(results);
    },
    { immediate: true },
);

watch(
    () => sessionStore.isRoundClosed,
    (isRoundClosed) => {
        clearPendingResultsCacheReset();

        if (isRoundClosed) {
            cachedSessionResults.value = cloneSessionResults(liveSessionResults.value);
            return;
        }

        if (cachedSessionResults.value === null) {
            return;
        }

        clearResultsCacheTimeout = setTimeout(() => {
            cachedSessionResults.value = null;
            clearResultsCacheTimeout = undefined;
        }, RESULTS_PANEL_EXIT_DURATION_MS);
    },
    { immediate: true },
);

watch(
    () => sessionStore.isRoundClosed,
    (isRoundClosed) => {
        if (isRoundClosed && Object.keys(sessionStore.statistics.groupedVotes).length === 1) {
            launchConfetti();
        }
    },
    { immediate: true },
);

onBeforeUnmount(() => {
    clearPendingResultsCacheReset();
});

function onConfirmEstimate(value: SessionResultValue) {
    // TODO: persist the facilitator-approved final estimate.
    console.log('Confirmed estimate:', value);
}

function finishRound() {
    if (sessionStore.isRoundClosed) {
        void sessionStore.finishRound();
    }
}

function replayRound() {
    if (sessionStore.isRoundClosed) {
        void sessionStore.replayRound();
    }
}
</script>

<template>
    <section class="flex h-full min-h-0 flex-col">
        <div class="min-h-0 flex-1 overflow-y-auto px-2 py-4">
            <SessionResults v-bind="displayedSessionResults" @confirm="onConfirmEstimate" />
        </div>
        <template v-if="pok48Flag.enabled">
            <PSeparator />
            <div class="flex flex-col gap-2 px-2 py-4 sm:flex-row sm:justify-end">
                <PButton variant="outline" type="button" @click="replayRound">Replay</PButton>
                <PButton type="button" @click="finishRound">Next Round</PButton>
            </div>
        </template>
    </section>
</template>
